import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { leadsApi, CrmIpdAdmittedRow } from '@/api/leads';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FilterMultiSelect } from '@/components/ui/filter-multi-select';
import {
  formatLeadStatusLabel,
  formatLeadSubStatusLabel,
} from '@/lib/crmLeadStyles';
import { LeadDetailModal } from '@/components/crm/LeadDetailModal';
import { toast } from 'sonner';
import type { Lead } from '@/api/leads';

const IST: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Kolkata' };

function formatIstDate(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', IST);
}

function formatIstDateTime(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function istDateKey(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

function matchesDateRange(iso: string | null | undefined, from: string, to: string): boolean {
  if (!from && !to) return true;
  const key = istDateKey(iso);
  if (!key) return false;
  if (from && key < from) return false;
  if (to && key > to) return false;
  return true;
}

function campaignOf(l: CrmIpdAdmittedRow): string {
  return (
    l.campaignLabel?.trim() ||
    l.campaignName?.trim() ||
    l.googleAdsUtmCampaign?.trim() ||
    ''
  );
}

function csvEscape(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatIpdStatusLabel(status?: string | null): string {
  if (status === 'ADMITTED') return 'Currently admitted';
  if (status === 'DISCHARGED') return 'Discharged';
  return status || '—';
}

export default function CrmIpdAdmittedPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('__all__');
  const [ipdStatusFilter, setIpdStatusFilter] = useState('__all__');
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [campaignFilters, setCampaignFilters] = useState<string[]>([]);
  const [admissionFrom, setAdmissionFrom] = useState('');
  const [admissionTo, setAdmissionTo] = useState('');
  const [dischargeFrom, setDischargeFrom] = useState('');
  const [dischargeTo, setDischargeTo] = useState('');
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadModalAnchor, setLeadModalAnchor] = useState<Lead | null>(null);

  const isSalesManager = user?.role === 'SALES_MANAGER';
  const canFilterByOwner =
    user?.role === 'HOSPITAL_ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    isSalesManager;

  const isSalesCrmRole =
    user?.role === 'SALES_PERSON' || user?.role === 'SALES_MANAGER';
  const showHospitalColumn = isSalesCrmRole;

  function leadHospitalLabel(l: CrmIpdAdmittedRow): string {
    return l.leadHospitalName?.trim() || l.ipdHospitalName?.trim() || '—';
  }

  const enabled = !!user?.hospitalId;
  const { data } = useQuery({
    queryKey: ['crm-ipd-admitted', user?.hospitalId],
    queryFn: async () => (await leadsApi.listSidebarIpdAdmitted({ limit: 2000 })).data.data.items,
    enabled,
    staleTime: 30_000,
  });

  const { data: staffOptions = [] } = useQuery({
    queryKey: ['crm-ipd-admitted-staff-options', user?.hospitalId, user?.id, isSalesManager],
    queryFn: async () => {
      try {
        const rows = await hospitalAdminApi.getStaff(
          isSalesManager ? { allHospitals: true, includeInactive: true } : undefined,
        );
        const salesPeople = (rows || []).filter(
          (s: { role?: string; status?: string; reportsToManagerId?: string | null; id?: string }) => {
            if (s?.role !== 'SALES_PERSON' && s?.role !== 'SALES_MANAGER') return false;
            if ((s?.status ?? 'ACTIVE') !== 'ACTIVE') return false;
            if (isSalesManager) {
              return s.reportsToManagerId === user?.id || s.id === user?.id;
            }
            return s.id === user?.id;
          },
        );
        if (isSalesManager && user?.id) {
          const self = (rows || []).find((s: { id?: string }) => s.id === user.id);
          if (self && !salesPeople.some((s: { id?: string }) => s.id === user.id)) {
            return [self, ...salesPeople];
          }
        }
        return salesPeople;
      } catch {
        return [];
      }
    },
    enabled: enabled && canFilterByOwner,
    staleTime: 60_000,
  });

  const { data: leadFilterOptions } = useQuery({
    queryKey: ['lead-filter-options', user?.hospitalId],
    queryFn: () => leadsApi.getLeadFilterOptions(),
    enabled,
    staleTime: 60_000,
  });

  const sourceFilterOptions = useMemo(
    () =>
      (leadFilterOptions?.sources ?? []).map((s) => ({
        value: s,
        label: s,
      })),
    [leadFilterOptions?.sources],
  );

  const campaignFilterOptions = useMemo(
    () =>
      (leadFilterOptions?.campaignNames ?? []).map((c) => ({
        value: c,
        label: c,
      })),
    [leadFilterOptions?.campaignNames],
  );

  const items = (data ?? []) as CrmIpdAdmittedRow[];

  const ownerFilterOptions = useMemo(() => {
    if (staffOptions.length > 0) return staffOptions;
    const byId = new Map<string, { id: string; name: string }>();
    for (const row of items) {
      if (row.assignedToId && row.assignedTo?.name) {
        byId.set(row.assignedToId, { id: row.assignedToId, name: row.assignedTo.name });
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [staffOptions, items]);

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const sourceSet = new Set(sourceFilters.map((s) => s.toUpperCase()));
    const campaignSet = new Set(campaignFilters.map((c) => c.trim().toLowerCase()));
    return items.filter((l) => {
      if (q) {
        const name = String(l?.patientName || '').toLowerCase();
        const phone = String(l?.phone || '');
        const alt = String(l?.alternatePhone || '');
        const ipdNo = String(l?.ipdNumber || '');
        if (!name.includes(q) && !phone.includes(q) && !alt.includes(q) && !ipdNo.includes(q)) {
          return false;
        }
      }

      if (canFilterByOwner && ownerFilter !== '__all__') {
        if (ownerFilter === '__none__') {
          if (l.assignedToId) return false;
        } else if (l.assignedToId !== ownerFilter) {
          return false;
        }
      }

      if (ipdStatusFilter === 'admitted' && l.ipdStatus !== 'ADMITTED') return false;
      if (ipdStatusFilter === 'discharged' && l.ipdStatus !== 'DISCHARGED') return false;

      if (sourceSet.size > 0) {
        const src = String(l.source || '').toUpperCase();
        if (!sourceSet.has(src)) return false;
      }

      if (campaignSet.size > 0) {
        const camp = campaignOf(l).toLowerCase();
        if (!camp || !campaignSet.has(camp)) return false;
      }

      if (!matchesDateRange(l.admissionDate, admissionFrom, admissionTo)) return false;
      if (!matchesDateRange(l.dischargeDate, dischargeFrom, dischargeTo)) return false;

      return true;
    });
  }, [
    items,
    search,
    ownerFilter,
    ipdStatusFilter,
    canFilterByOwner,
    sourceFilters,
    campaignFilters,
    admissionFrom,
    admissionTo,
    dischargeFrom,
    dischargeTo,
  ]);

  const hasFilters =
    admissionFrom ||
    admissionTo ||
    dischargeFrom ||
    dischargeTo ||
    sourceFilters.length ||
    campaignFilters.length ||
    ipdStatusFilter !== '__all__';

  const clearFilters = () => {
    setAdmissionFrom('');
    setAdmissionTo('');
    setDischargeFrom('');
    setDischargeTo('');
    setSourceFilters([]);
    setCampaignFilters([]);
    setIpdStatusFilter('__all__');
  };

  const downloadCsv = () => {
    if (filtered.length === 0) {
      toast.error('No rows to download.');
      return;
    }
    const headers = [
      'Patient',
      'Phone',
      'IPD number',
      'Doctor',
      ...(showHospitalColumn ? ['Lead hospital'] : []),
      'IPD hospital',
      'Owner',
      'Owner hospital',
      'Source',
      'Campaign',
      'IPD status',
      'Admission date',
      'Discharge date',
      'Surgery',
      'Lead status',
      'Lead sub status',
    ];
    const lines = [headers.map(csvEscape).join(',')];
    for (const l of filtered) {
      lines.push(
        [
          l.patientName,
          l.phone,
          l.ipdNumber ?? '',
          l.ipdDoctorName?.trim() || '',
          ...(showHospitalColumn ? [leadHospitalLabel(l)] : []),
          l.ipdHospitalName?.trim() || '',
          l.assignedTo?.name ?? 'Unassigned',
          l.leadHospitalName?.trim() || '',
          l.source || '',
          campaignOf(l),
          formatIpdStatusLabel(l.ipdStatus),
          formatIstDate(l.admissionDate),
          formatIstDate(l.dischargeDate),
          l.surgeryName ?? '',
          formatLeadStatusLabel(l.status),
          l.leadSubStatus ? formatLeadSubStatusLabel(l.leadSubStatus) : '',
        ]
          .map(csvEscape)
          .join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ipd-admitted-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    toast.success(`Downloaded ${filtered.length} row(s)`);
  };

  const navLeads = useMemo(
    () =>
      filtered.map(
        (r) =>
          ({
            id: r.leadId,
            patientName: r.patientName,
            phone: r.phone,
          }) as Lead,
      ),
    [filtered],
  );

  return (
    <div className="p-6 space-y-4">
      <LeadDetailModal
        open={leadModalOpen}
        onOpenChange={(o) => {
          setLeadModalOpen(o);
          if (!o) setLeadModalAnchor(null);
        }}
        anchorLead={leadModalAnchor}
        navLeads={navLeads}
      />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">IPD Admitted</h1>
          <p className="text-xs text-slate-500 mt-1">
            {isSalesManager
              ? 'CRM leads owned by you or your direct reports, with actual IPD admissions across chain hospitals — current and discharged. Matched by patient phone.'
              : user?.role === 'SALES_PERSON'
                ? 'Your assigned CRM leads with IPD admissions across chain hospitals — current and discharged. Matched by patient phone.'
                : 'CRM leads with IPD admissions (current and discharged). Filter by owner, source, admission dates, or search by patient.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search by name, phone, or IPD no."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72 h-9"
          />
          <Button type="button" variant="outline" className="h-9 gap-1.5" onClick={downloadCsv}>
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-slate-50/80 px-3 py-3">
        {canFilterByOwner ? (
          <div>
            <Label className="text-[11px] text-slate-500">Owner</Label>
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[12rem] h-9 text-sm bg-white">
                <SelectValue placeholder="All owners" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="__all__">All owners</SelectItem>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {ownerFilterOptions.map((s: { id: string; name: string }) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div>
          <Label className="text-[11px] text-slate-500">IPD status</Label>
          <Select value={ipdStatusFilter} onValueChange={setIpdStatusFilter}>
            <SelectTrigger className="w-[11rem] h-9 text-sm bg-white">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All admissions</SelectItem>
              <SelectItem value="admitted">Currently admitted</SelectItem>
              <SelectItem value="discharged">Discharged</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[11px] text-slate-500">Source</Label>
          <FilterMultiSelect
            values={sourceFilters}
            onValuesChange={setSourceFilters}
            options={sourceFilterOptions}
            placeholder="Source"
            allLabel="All sources"
            triggerClassName="w-[11rem] bg-white"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Campaign</Label>
          <FilterMultiSelect
            values={campaignFilters}
            onValuesChange={setCampaignFilters}
            options={campaignFilterOptions}
            placeholder="Campaign"
            allLabel="All campaigns"
            triggerClassName="w-[12rem] bg-white"
          />
        </div>

        <div>
          <Label className="text-[11px] text-slate-500">Admission from</Label>
          <Input
            type="date"
            value={admissionFrom}
            onChange={(e) => setAdmissionFrom(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Admission to</Label>
          <Input
            type="date"
            value={admissionTo}
            onChange={(e) => setAdmissionTo(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>

        <div>
          <Label className="text-[11px] text-slate-500">Discharge from</Label>
          <Input
            type="date"
            value={dischargeFrom}
            onChange={(e) => setDischargeFrom(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Discharge to</Label>
          <Input
            type="date"
            value={dischargeTo}
            onChange={(e) => setDischargeTo(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>

        {hasFilters ? (
          <Button type="button" variant="outline" className="h-9" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}
      </div>

      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="max-h-[75vh] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="text-xs text-slate-500 border-b">
                <th className="px-3 py-2 text-left font-medium">Patient</th>
                <th className="px-3 py-2 text-left font-medium">IPD no.</th>
                  <th className="px-3 py-2 text-left font-medium">Doctor</th>
                {showHospitalColumn ? (
                  <th className="px-3 py-2 text-left font-medium">Lead hospital</th>
                ) : null}
                <th className="px-3 py-2 text-left font-medium">IPD hospital</th>
                <th className="px-3 py-2 text-left font-medium">Owner</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
                <th className="px-3 py-2 text-left font-medium">Campaign</th>
                <th className="px-3 py-2 text-left font-medium">IPD status</th>
                <th className="px-3 py-2 text-left font-medium">Admission</th>
                <th className="px-3 py-2 text-left font-medium">Discharge</th>
                <th className="px-3 py-2 text-left font-medium">Surgery</th>
                <th className="px-3 py-2 text-left font-medium">Lead status</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const campaign = campaignOf(l);
                const statusLabel = formatLeadStatusLabel(l.status);
                const subLabel = l.leadSubStatus
                  ? formatLeadSubStatusLabel(l.leadSubStatus)
                  : null;
                const doctorLabel = l.ipdDoctorName?.trim() || '—';
                const rowKey = `${l.leadId}-${l.admissionId}`;
                return (
                  <tr key={rowKey} className="border-b last:border-b-0 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{l.patientName}</div>
                      <div className="text-xs text-slate-500">{l.phone}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                      {l.ipdNumber?.trim() || '—'}
                    </td>
                    <td
                      className="px-3 py-2 text-slate-700 max-w-[9rem] truncate"
                      title={doctorLabel}
                    >
                      {doctorLabel}
                    </td>
                    {showHospitalColumn ? (
                      <td
                        className="px-3 py-2 text-slate-700 max-w-[10rem] truncate"
                        title={leadHospitalLabel(l)}
                      >
                        {leadHospitalLabel(l)}
                      </td>
                    ) : null}
                    <td
                      className="px-3 py-2 text-slate-700 max-w-[10rem] truncate"
                      title={l.ipdHospitalName ?? undefined}
                    >
                      {l.ipdHospitalName?.trim() || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="font-medium">{l.assignedTo?.name ?? 'Unassigned'}</div>
                      {l.leadHospitalName?.trim() ? (
                        <div className="text-xs text-slate-500 mt-0.5">{l.leadHospitalName}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{l.source || '—'}</td>
                    <td
                      className="px-3 py-2 text-slate-700 max-w-[10rem] truncate"
                      title={campaign || undefined}
                    >
                      {campaign || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <span
                        className={
                          l.ipdStatus === 'ADMITTED'
                            ? 'inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-600/20'
                            : 'inline-flex rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-500/20'
                        }
                      >
                        {formatIpdStatusLabel(l.ipdStatus)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                      {formatIstDateTime(l.admissionDate) || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                      {formatIstDate(l.dischargeDate) || '—'}
                    </td>
                    <td
                      className="px-3 py-2 text-slate-700 max-w-[10rem] truncate"
                      title={l.surgeryName ?? undefined}
                    >
                      {l.surgeryName?.trim() || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="font-medium">{statusLabel}</div>
                      {subLabel ? (
                        <div className="text-xs text-slate-500 mt-0.5">{subLabel}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => {
                          setLeadModalAnchor({
                            id: l.leadId,
                            patientName: l.patientName,
                            phone: l.phone,
                          } as Lead);
                          setLeadModalOpen(true);
                        }}
                      >
                        Open lead
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={showHospitalColumn ? 14 : 13}
                    className="px-3 py-8 text-center text-slate-500"
                  >
                    No IPD admissions found for your CRM leads.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
